import { ListItemIcon } from '@mui/material';
import { useMemo } from 'react';

import { SemanticTileKindNode } from '@/components/diagrams/components/Tile/Tile.utils';
import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { User01Icon } from '@/components/icons/User01Icon';
import {
  DispositiveProvisionsPossibleRecipients_EntityFragment,
  useDispositiveProvisionsPossibleRecipientsQuery,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/graphql/DispositiveProvisionsFormRecipients.generated';
import {
  buildRecipientTypeaheadOptions,
  filterPossibleRecipientEntities,
  SURVIVING_SPOUSE_SENTINEL,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/useRecipientOptions';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { formatCreatedFromEntities } from '@/modules/entities/testamentaryEntities/testamentaryEntities.utils';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  ClientOrganizationKind,
  EntityInEstateStatus,
  EntityStage,
  TestamentaryEntityKind,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

/** BDG-form metadata with recipient look-through ownership details. */
export type BdgRecipientMetadata = SemanticTileKindNode & {
  /**
   * Owner client ids for "look-through" account recipients (retirement,
   * individual/direct-holdings, joint). Lets the form's active-tier preview
   * cascade a designation to the contingent tier once every owner has
   * predeceased, matching the diagram resolver (T1-6171). Absent for kinds
   * we don't look through.
   */
  lookThroughOwnerIds?: string[];
};

/**
 * Owner client ids for the recipient kinds whose owner death cascades a
 * beneficiary designation to the contingent tier (retirement,
 * individual/direct-holdings, joint — T1-6171). Joint accounts return all
 * owners; the tier check treats the recipient as dead only once every one of
 * them has died. Returns undefined for all other entity kinds.
 */
function getRecipientLookThroughOwnerIds(
  entity: DispositiveProvisionsPossibleRecipients_EntityFragment
): string[] | undefined {
  const subtype = entity.subtype;
  switch (subtype.__typename) {
    case 'RetirementPersonalAccount':
    case 'IndividualPersonalAccount': {
      const ownerId = subtype.owner?.individual?.id;
      return ownerId ? [ownerId] : undefined;
    }
    case 'JointPersonalAccount': {
      const ownerIds = (subtype.owners ?? [])
        .map((owner) => owner?.individual?.id)
        .filter((id): id is string => Boolean(id));
      return ownerIds.length > 0 ? ownerIds : undefined;
    }
    default:
      return undefined;
  }
}

/**
 * Provides a flat list of possible recipient options for the beneficiary
 * designation form. Simplified version of the DP form's useRecipientOptions
 * that does not depend on DispositiveProvisionsContext.
 */
export function useBeneficiaryDesignationRecipientOptions({
  entityIdOverride,
  isOnHypotheticalWaterfall = false,
}: {
  entityIdOverride?: string;
  /** When on a hypothetical waterfall, surface Draft-stage entities as a
   * "Draft Entities" group, matching the DP recipient dropdown. */
  isOnHypotheticalWaterfall?: boolean;
} = {}) {
  const { householdId, primaryClients, isTwoClientHousehold } =
    useHouseholdDetailsContext();
  const { entityId: contextEntityId } = useEntityDetailsContext();
  const entityId = entityIdOverride ?? contextEntityId;

  const { data } = useDispositiveProvisionsPossibleRecipientsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      where: { id: householdId },
      hasDeadGrantors: true,
    },
  });

  const household = getNodes(data?.households)[0];

  return useMemo(() => {
    if (!household) {
      return {
        options: [],
        typeaheadOptions: [],
        metadataById: {},
        recipientLists: {
          clientProfileRecipients: [],
          entityRecipients: [],
          organizationRecipients: [],
          testamentaryEntityRecipients: [],
        },
      };
    }

    const metadataById: Record<string, BdgRecipientMetadata> = {};

    // Mirror the DP recipient chooser: a two-client household can designate
    // the surviving spouse, resolved at death time rather than to a specific
    // person. Surfaced as the first option with a person icon.
    const survivingSpouseOption: SelectInputOption<string> | null =
      isTwoClientHousehold
        ? {
            display: 'Surviving Spouse',
            value: SURVIVING_SPOUSE_SENTINEL,
            startAdornment: (
              <ListItemIcon>
                <User01Icon size={16} />
              </ListItemIcon>
            ),
          }
        : null;
    if (survivingSpouseOption) {
      metadataById[SURVIVING_SPOUSE_SENTINEL] = {
        __typename: 'ClientProfile',
        inEstateStatus: EntityInEstateStatus.InEstate,
      };
    }

    const clientProfileRecipients = household.clientProfiles ?? [];
    const entityRecipients = getNodes(household.entities);
    const organizationRecipients = household.clientOrganizations ?? [];

    const clientProfileOptions = clientProfileRecipients
      .map((c) => {
        metadataById[c.id] = {
          __typename: 'ClientProfile',
          inEstateStatus: primaryClients?.some((p) => p.id === c.id)
            ? EntityInEstateStatus.InEstate
            : EntityInEstateStatus.OutOfEstate,
        };
        return { display: c.displayName, value: c.id };
      })
      .sort((a, b) => a.display.localeCompare(b.display));

    // Records the entity's tile metadata as a side effect, then returns its
    // option. Shared by the active and draft (hypothetical) entity groups.
    const toEntityRecipientOption = (
      e: DispositiveProvisionsPossibleRecipients_EntityFragment
    ) => {
      metadataById[e.id] = {
        __typename: 'Entity',
        entityKind: e.kind,
        inEstateStatus:
          'inEstateStatus' in e.subtype
            ? (e.subtype.inEstateStatus ?? undefined)
            : undefined,
        lookThroughOwnerIds: getRecipientLookThroughOwnerIds(e),
      };
      return { display: e.subtype.displayName, value: e.id };
    };

    // Reuse the same filter the DP form uses so eligibility rules stay
    // aligned (e.g. excluded stages, business-entity types).
    const entityRecipientOptions = filterPossibleRecipientEntities({
      entities: entityRecipients,
      includeStages: [EntityStage.Active, EntityStage.Completed],
      excludeEntities: entityId ? [entityId] : undefined,
    })
      .map(toEntityRecipientOption)
      .sort((a, b) => a.display.localeCompare(b.display));

    // Draft entities only exist in a hypothetical waterfall scope; surface them
    // as their own group there, matching the DP recipient dropdown.
    const draftEntityRecipientOptions = isOnHypotheticalWaterfall
      ? filterPossibleRecipientEntities({
          entities: entityRecipients,
          includeStages: [EntityStage.Draft],
          excludeEntities: entityId ? [entityId] : undefined,
        })
          .map(toEntityRecipientOption)
          .sort((a, b) => a.display.localeCompare(b.display))
      : [];

    const organizationRecipientOptions = organizationRecipients
      .map((c) => {
        metadataById[c.id] = {
          __typename: 'ClientOrganization',
          isCharitableOrganization:
            c.kind === ClientOrganizationKind.CharitableOrganization,
        };
        return { display: c.name, value: c.id };
      })
      .sort((a, b) => a.display.localeCompare(b.display));

    const testamentaryEntityOptions = getNodes(
      household.testamentaryEntities
    ).map((te) => {
      metadataById[te.id] = {
        __typename: 'TestamentaryEntity',
        inEstateStatus: te.inEstateStatus ?? undefined,
        testamentaryEntityKind: te.kind,
        // Inherited retirement accounts look through to their account owner
        // (the surviving owner the account is for) — T1-6171.
        lookThroughOwnerIds:
          te.kind === TestamentaryEntityKind.InheritedRetirementAccount &&
          te.accountOwner?.id
            ? [te.accountOwner.id]
            : undefined,
      };
      // Sub-trust "created from …" subtext, matching the DP recipient dropdown.
      return {
        display: te.displayName,
        value: te.id,
        secondaryText:
          formatCreatedFromEntities(te.createdFromEntities) ?? undefined,
      };
    });

    const options: SelectInputOption<string>[] = [
      ...(survivingSpouseOption ? [survivingSpouseOption] : []),
      ...clientProfileOptions,
      ...entityRecipientOptions,
      ...organizationRecipientOptions,
      ...testamentaryEntityOptions,
      ...draftEntityRecipientOptions,
    ];

    // Flat, grouped-by-`groupName` variant for the typeahead recipient dropdown.
    // Section order mirrors the DP recipient chooser (Individuals, Organizations,
    // Sub-trusts, Entities, Draft Entities) so the two forms stay visually
    // consistent;
    // Surviving Spouse leads without a group so it renders header-less. The flat
    // `options` above is kept for value→display lookups, since header rows carry
    // no value.
    const typeaheadOptions = buildRecipientTypeaheadOptions(
      [
        { groupName: 'Individuals', options: clientProfileOptions },
        { groupName: 'Organizations', options: organizationRecipientOptions },
        { groupName: 'Sub-trusts', options: testamentaryEntityOptions },
        { groupName: 'Entities', options: entityRecipientOptions },
        { groupName: 'Draft Entities', options: draftEntityRecipientOptions },
      ],
      survivingSpouseOption
        ? {
            display: 'Surviving Spouse',
            value: SURVIVING_SPOUSE_SENTINEL,
            startAdornment: (
              <ListItemIcon>
                <User01Icon size={16} />
              </ListItemIcon>
            ),
          }
        : null
    );

    // The same possible-recipients query powers the DP recipient-type
    // defaulting logic. Surface the raw fragment lists so the BDG default
    // hook can reuse `_getDefaultedKindForRecipient` without depending on
    // DispositiveProvisionsContext.
    const recipientLists = {
      clientProfileRecipients,
      entityRecipients,
      organizationRecipients,
      testamentaryEntityRecipients: getNodes(household.testamentaryEntities),
    };

    return {
      options,
      typeaheadOptions,
      metadataById,
      recipientLists,
    };
  }, [
    household,
    entityId,
    primaryClients,
    isTwoClientHousehold,
    isOnHypotheticalWaterfall,
  ]);
}
